import { api, ensureCsrfCookie } from '@/api/client';
import type {
  Invitation,
  Member,
  Organization,
  Paginated,
  Permission,
  Role,
  User,
} from '@/types/api';

// --- Members -----------------------------------------------------------------

export async function fetchMembers(): Promise<Paginated<Member>> {
  const { data } = await api.get<Paginated<Member>>('/members');
  return data;
}

export async function updateMemberRole(membershipId: string, roleId: string): Promise<void> {
  await ensureCsrfCookie();
  await api.patch(`/members/${membershipId}`, { role_id: roleId });
}

export async function removeMember(membershipId: string): Promise<void> {
  await ensureCsrfCookie();
  await api.delete(`/members/${membershipId}`);
}

// --- Invitations ---------------------------------------------------------------

export async function fetchInvitations(): Promise<Paginated<Invitation>> {
  const { data } = await api.get<Paginated<Invitation>>('/invitations');
  return data;
}

export async function createInvitation(email: string, roleId: string): Promise<Invitation> {
  await ensureCsrfCookie();
  const { data } = await api.post<{ invitation: Invitation }>('/invitations', {
    email,
    role_id: roleId,
  });
  return data.invitation;
}

export async function revokeInvitation(id: string): Promise<void> {
  await ensureCsrfCookie();
  await api.delete(`/invitations/${id}`);
}

/**
 * Redeem an invitation. PUBLIC — the token is the credential.
 *
 * name and password are required only when the invited address has no
 * account yet; the server decides and reports which case applies.
 */
export interface AcceptInvitationPayload {
  token: string;
  /** Required only when the invited address has no account yet. */
  name?: string;
  password?: string;
  password_confirmation?: string;
}

export interface AcceptInvitationResponse {
  user: User;
  membership: Member & { organization: Organization };
  account_created: boolean;
}

export async function acceptInvitation(
  payload: AcceptInvitationPayload,
): Promise<AcceptInvitationResponse> {
  await ensureCsrfCookie();
  const { data } = await api.post<AcceptInvitationResponse>('/invitations/accept', payload);
  return data;
}

// --- Roles -----------------------------------------------------------------------

export async function fetchRoles(): Promise<Role[]> {
  const { data } = await api.get<{ data: Role[] }>('/roles');
  return data.data;
}

export async function fetchPermissions(): Promise<Permission[]> {
  const { data } = await api.get<{ data: Permission[] }>('/permissions');
  return data.data;
}
